/**
 * Phase 13 Step 5 — Slice Pk table.
 *
 * Header copy uses the defense-professional wording "Slice Pk" rather
 * than "heatmap cell". Each row is one GeometrySliceResult from the
 * backend: slice_id, n, Pk mean, 95 % CI, centroid.
 *
 * Sortable by Pk (asc/desc) and slice_id. Pure presentation.
 */
import { useMemo, useState } from "react";
import { ArrowUpDown } from "lucide-react";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";

function fmtPk(v) {
    if (v == null || Number.isNaN(Number(v))) return "—";
    return Number(v).toFixed(3);
}

export default function SliceTable({ campaign }) {
    // Phase 14.8 W5 — wrap `slices` in useMemo so its identity is
    // stable across renders when the underlying `result.slices`
    // hasn't changed; otherwise the dependent useMemo at line 34
    // recomputes on every render.
    const slices = useMemo(() => campaign?.result?.slices || [], [campaign?.result?.slices]);
    const [sortKey, setSortKey] = useState("slice_id");
    const [sortDir, setSortDir] = useState("asc");

    const rows = useMemo(() => {
        const copy = [...slices];
        copy.sort((a, b) => {
            const d = (a[sortKey] ?? 0) - (b[sortKey] ?? 0);
            return sortDir === "asc" ? d : -d;
        });
        return copy;
    }, [slices, sortKey, sortDir]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    if (!slices.length) {
        return (
            <div
                className="rounded-md border border-ux-border-1 bg-ux-surface-1 p-6 text-center text-sm text-muted-foreground"
                data-testid="geom-slice-table-empty"
            >
                No slice data yet. Slice-by-slice Pk appears once the campaign completes.
            </div>
        );
    }
    return (
        <div className="rounded-md border border-ux-border-1 bg-ux-surface-1">
            <Table data-testid="geom-slice-table">
                <TableHeader>
                    <TableRow>
                        <TableHead>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="-ml-2 gap-1"
                                onClick={() => toggleSort("slice_id")}
                                data-testid="geom-slice-sort-slice-id"
                            >
                                Slice <ArrowUpDown className="size-3" />
                            </Button>
                        </TableHead>
                        <TableHead className="text-center">N</TableHead>
                        <TableHead className="text-right">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="-mr-2 gap-1"
                                onClick={() => toggleSort("pk_mean")}
                                data-testid="geom-slice-sort-pk"
                            >
                                Slice Pk <ArrowUpDown className="size-3" />
                            </Button>
                        </TableHead>
                        <TableHead className="text-right">95 % CI</TableHead>
                        <TableHead>Centroid (range × bearing × alt)</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((sl) => (
                        <TableRow
                            key={sl.slice_id}
                            data-testid={`geom-slice-row-${sl.slice_id}`}
                        >
                            <TableCell className="tabular-nums">{sl.slice_id}</TableCell>
                            <TableCell className="text-center tabular-nums">{sl.n}</TableCell>
                            <TableCell className="text-right tabular-nums">
                                {fmtPk(sl.pk_mean)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-muted-foreground">
                                [{fmtPk(sl.pk_ci_lower)}, {fmtPk(sl.pk_ci_upper)}]
                            </TableCell>
                            <TableCell className="text-xs tabular-nums text-muted-foreground">
                                {sl.geometry_centroid
                                    ? `${Math.round(sl.geometry_centroid.range_m)}m · `
                                      + `${Math.round(sl.geometry_centroid.bearing_deg)}° · `
                                      + `${Math.round(sl.geometry_centroid.altitude_m)}m`
                                    : "—"}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
